import React, { useEffect, useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route } from 'react-router-dom';

import { HOME_PATH, LOGIN_PATH, SUBSCRIPTION_EXPIRED_PATH } from '../constants';
import { isUserAuthenticated, getLoggedInUser } from '../helpers/authUtils';
import { shouldRedirectToSubscriptionExpired } from '../helpers/subscriptionLockout';
import { clearLoginRedirect, initMenu, initProfileMenu, loadUserSubscriptionInformation } from '../redux/actions';

// Follows the address saved at sign-in and clears it (store and session cookie) in the
// same commit, so it is used once: later visits to '/' stay home.
const LoginRedirect = ({ to, onConsumed }) => {
  useLayoutEffect(() => {
    onConsumed();
  }, [onConsumed]);
  // String form so a query in the redirect (e.g. a Share Link's ?via=) stays a query.
  return <Redirect to={to} />;
};

export const MenuOptions = Object.freeze({
  default: 0,
  profile: 1,
});

// handle auth and authorization
const PrivateRouteInternal = ({
  component: Component,
  roles,
  menuOption,
  user,
  subscriptions,
  subscriptionLoading,
  loadUserSubscriptionInformation: loadSubscriptions,
  clearLoginRedirect: consumeLoginRedirect,
  ...rest
}) => {
  useEffect(() => {
    switch (menuOption) {
      case MenuOptions.profile:
        rest.initProfileMenu();
        break;
      default:
        rest.initMenu();
    }
  }, []);

  useEffect(() => {
    if (isUserAuthenticated() && !subscriptions && !subscriptionLoading) {
      loadSubscriptions();
    }
  }, [subscriptions, subscriptionLoading, loadSubscriptions]);

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!isUserAuthenticated()) {
          window.location.href = LOGIN_PATH;
          return null;
        }

        const loggedInUser = user ?? getLoggedInUser();

        if (roles && roles.indexOf(loggedInUser.role) === -1) {
          return <Redirect to={{ pathname: HOME_PATH }} />;
        }

        if (
          shouldRedirectToSubscriptionExpired(
            loggedInUser,
            subscriptions,
            props.location.pathname
          )
        ) {
          return <Redirect to={{ pathname: SUBSCRIPTION_EXPIRED_PATH }} />;
        }

        if (loggedInUser.redirectUrl && props.location.pathname === HOME_PATH) {
          return <LoginRedirect to={loggedInUser.redirectUrl} onConsumed={consumeLoginRedirect} />;
        }

        return <Component {...props} />;
      }}
    />
  );
};

const mapStateToProps = (state) => ({
  user: state.Auth?.user,
  subscriptions: state.User?.subscriptions,
  subscriptionLoading: state.User?.subscriptionLoading,
});

export const PrivateRoute = connect(mapStateToProps, {
  initProfileMenu,
  initMenu,
  loadUserSubscriptionInformation,
  clearLoginRedirect,
})(PrivateRouteInternal);

const PublicAndPrivateRouteInternal = ({ component: Component, roles, menuOption, ...rest }) => {
  useEffect(() => {
    switch (menuOption) {
      case MenuOptions.profile:
        rest.initProfileMenu();
        break;
      default:
        rest.initMenu();
    }
  }, []);

  return <Route {...rest} render={(props) => <Component {...props} />} />;
};

export const PublicAndPrivateRoute = connect(null, { initProfileMenu, initMenu })(
  PublicAndPrivateRouteInternal
);
